import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, User, Package, Clock, RefreshCw, CheckSquare, Trash2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useContactRequests } from '../hooks/useAdmin';
import { useVehicles } from '../hooks/useVehicles';
import AdminHeader from '../components/AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { requests, loading: requestsLoading, refetch: refetchRequests, invalidateCache: invalidateRequestsCache } = useContactRequests();
  const { vehicles, loading: vehiclesLoading } = useVehicles();
  const loading = requestsLoading || vehiclesLoading;
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    console.log('Admin - Checking auth, token exists:', !!token);
    
    if (!token) {
      console.log('Admin - No token found, redirecting to login');
      navigate('/login', { replace: true });
      return false;
    }
    
    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Admin - Token valid:', response.data);
      return true;
    } catch (error) {
      console.log('Admin - Token invalid, removing and redirecting');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_token_time');
      navigate('/login', { replace: true });
      return false;
    }
  };

  const handleCreateBooking = async () => {
    if (!selectedVehicleId) {
      toast.error("Bitte wählen Sie ein Fahrzeug aus");
      return;
    }

    const token = localStorage.getItem('admin_token');
    const request = requests.find(r => r.id === selectedRequest.id);
    
    try {
      await axios.post(`${API}/bookings/`, {
        contact_request_id: selectedRequest.id,
        vehicle_id: selectedVehicleId,
        start_date: request.start_date,
        end_date: request.end_date,
        notes: request.message || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Buchung erfolgreich erstellt!");
      setIsBookingDialogOpen(false);
      setSelectedRequest(null);
      setSelectedVehicleId(null);
      await refetchRequests(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Buchung");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/contact/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Anfrage erfolgreich gelöscht!");
      invalidateRequestsCache();
      await refetchRequests(true);
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error("Fehler beim Löschen");
    }
  };

  useEffect(() => {
    // Force scroll to top on mount
    window.scrollTo(0, 0);
    
    const init = async () => {
      await checkAuth();
      // Data is already being fetched by hooks
    };
    init();
  }, []); // eslint-disable-line

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'processed':
        return 'Bearbeitet';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Admin" subtitle="Anfragen" />

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C3E50]">Admin - Anfragen-Übersicht</h1>
            <p className="text-gray-600 mt-2">Alle Kontaktanfragen im Überblick</p>
          </div>
          <Button 
            onClick={() => refetchRequests(true)} 
            className="bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] w-full sm:w-auto"
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 animate-spin mx-auto text-[#FDB913] mb-4" />
            <p className="text-gray-600">Lade Anfragen...</p>
          </div>
        ) : requests.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Noch keine Anfragen vorhanden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {requests.map((request) => (
              <Card key={request.id} className="border-2 hover:border-[#FDB913] transition-all">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-2xl text-[#2C3E50] flex items-center gap-3">
                        <User className="h-6 w-6 text-[#FDB913]" />
                        {request.name}
                      </CardTitle>
                      <CardDescription className="mt-2 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Eingegangen: {formatDateTime(request.created_at)}
                      </CardDescription>
                    </div>
                    <Badge className={`${getStatusColor(request.status)} text-white`}>
                      {getStatusText(request.status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-[#FDB913]" />
                        <div>
                          <p className="text-sm text-gray-500">E-Mail</p>
                          <p className="font-medium">{request.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-[#FDB913]" />
                        <div>
                          <p className="text-sm text-gray-500">Telefon</p>
                          <p className="font-medium">{request.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-[#FDB913]" />
                        <div>
                          <p className="text-sm text-gray-500">Gewünschtes Fahrzeug/Maschine</p>
                          <p className="font-medium">{request.vehicle}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-[#FDB913]" />
                        <div>
                          <p className="text-sm text-gray-500">Mietdauer</p>
                          <p className="font-medium">
                            {formatDate(request.start_date)} bis {formatDate(request.end_date)}
                          </p>
                        </div>
                      </div>
                      {request.message && (
                        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                          <p className="text-sm text-gray-500 mb-2">Nachricht:</p>
                          <p className="text-gray-700">{request.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3 flex-wrap">
                    <Button 
                      variant="outline" 
                      className="border-[#2C3E50] hover:bg-[#FDB913] hover:text-[#1a1a1a]"
                      onClick={() => window.location.href = `tel:${request.phone}`}
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Anrufen
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-[#2C3E50] hover:bg-[#FDB913] hover:text-[#1a1a1a]"
                      onClick={() => window.location.href = `mailto:${request.email}`}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      E-Mail senden
                    </Button>
                    {request.status !== 'booked' && (
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          setSelectedRequest(request);
                          setSelectedVehicleId(null);
                          setIsBookingDialogOpen(true);
                        }}
                      >
                        <CheckSquare className="mr-2 h-4 w-4" />
                        In Buchung umwandeln
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      onClick={() => handleDeleteRequest(request.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Löschen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8 text-center text-gray-500">
          <p>Gesamt: {requests.length} Anfrage(n)</p>
        </div>
      </div>

      {/* Booking Creation Dialog */}
      <Dialog open={isBookingDialogOpen} onOpenChange={setIsBookingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fuhrpark</DialogTitle>
            <DialogDescription>
              Wählen Sie ein Fahrzeug für diese Anfrage aus
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded">
                <p className="font-semibold">{selectedRequest.name}</p>
                <p className="text-sm text-gray-600">{selectedRequest.vehicle}</p>
                <p className="text-sm text-gray-600">
                  {selectedRequest.start_date} bis {selectedRequest.end_date}
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Fahrzeug auswählen</label>
                <select
                  value={selectedVehicleId || ""}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
                >
                  <option value="">Fahrzeug wählen...</option>
                  {vehicles.filter(v => v.available).map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.name} ({vehicle.category}) - Bestand: {vehicle.quantity || 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleCreateBooking}
                  className="flex-1 bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a]"
                  disabled={!selectedVehicleId}
                >
                  Buchen
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsBookingDialogOpen(false);
                    setSelectedRequest(null);
                    setSelectedVehicleId(null);
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
